import type { ReactNode, SyntheticEvent } from "react"
import React, { lazy, Suspense, useEffect, useRef, useState } from "react"
import { CLabel } from "./CLabel"
import { CToggleSwitch } from "./CToggleSwitch"
import {
  type Control,
  Controller,
  type UseFormGetValues,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form"
import { CAutoSizeTextArea } from "./CAutoSizeTextArea"
import { linkifyText } from "./linkifyText"
import { externalLinkRenderer } from "./externalLink"
import { useTranslation } from "react-i18next"
import { PencilGrayIcon } from "@shared/icons"
import useDevice from "@shared/hooks/useDevice"
import { TextFormat, type L10nFragment } from "@/graphql/main/gql/graphql"
import type { FormattedText } from "./richText/markdown"
import { L10nEditWarning } from "./l10n/L10nEditWarning"
import { L10nNote } from "./l10n/L10nNote"
import { resolveL10n } from "./l10n/fn"

// Only a text with a format ever needs these, and only once it is MARKDOWN or being edited: the
// Markdown reader and the ProseMirror stack stay out of every chunk that merely shows a plain text.
const MarkdownText = lazy(async () => await import("./richText/MarkdownText"))
const RichTextEditor = lazy(async () => await import("./richText/RichTextEditor"))

interface ReadOnlyTextProps {
  text: string
  isMarkdown: boolean
  isIOSApp: boolean
  emptyText: string
}

/** The text for reading: Markdown formatted, a plain text as typed with its addresses clickable. */
const ReadOnlyText = ({ text, isMarkdown, isIOSApp, emptyText }: ReadOnlyTextProps) => {
  if (!text) return <span className="text-gray-500">{emptyText}</span>
  if (isMarkdown) {
    return (
      <Suspense fallback={null}>
        <MarkdownText text={text} />
      </Suspense>
    )
  }
  return <>{linkifyText(text, externalLinkRenderer(isIOSApp))}</>
}

interface RichTextFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  valName: Path<TFieldValues>
  formatName: Path<TFieldValues>
  required?: boolean
  /** What the editor opens with instead of the stored text — the draft, or the translation shown. */
  shownText: string | null
  placeholder: string
  disabled?: boolean
  onDraft: (text: string) => void
  isEditing: boolean
  renderReadOnly: (text: string, isMarkdown: boolean) => ReactNode
}

/** Both fields stay bound in preview too, so a refetch cannot change the format of a local draft. */
const RichTextField = <TFieldValues extends FieldValues>({
  control,
  valName,
  formatName,
  required,
  shownText,
  placeholder,
  disabled,
  onDraft,
  isEditing,
  renderReadOnly,
}: RichTextFieldProps<TFieldValues>) => {
  const text = useController({ control, name: valName, rules: { required } })
  const format = useController({ control, name: formatName })
  // The format the draft was written in. A refetch resets the form keeping only dirty fields, and a
  // draft edited in its old format has a dirty text but a clean format (writing the same value
  // leaves it clean): the reset would pair the draft with the format the server now holds for
  // other words. While the text is a draft its format stays the draft's, and becomes dirty with it.
  const draftFormat = useRef<TextFormat | null>(null)
  const isDraft = text.fieldState.isDirty
  const { value: currentFormat, onChange: setFormat } = format.field
  useEffect(() => {
    if (!isDraft) {
      draftFormat.current = null
      return
    }
    if (draftFormat.current !== null && currentFormat !== draftFormat.current) setFormat(draftFormat.current)
  }, [isDraft, currentFormat, setFormat])
  const storedText: unknown = text.field.value
  const storedFormat: unknown = format.field.value
  const value: FormattedText = {
    text: shownText ?? (typeof storedText === "string" ? storedText : ""),
    format: storedFormat === TextFormat.Markdown ? TextFormat.Markdown : TextFormat.Plain,
  }

  if (!isEditing) {
    return renderReadOnly(typeof storedText === "string" ? storedText : "", value.format === TextFormat.Markdown)
  }

  return (
    <Suspense fallback={<div className="min-h-34 rounded-lg border border-gray-300" />}>
      <RichTextEditor
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(next) => {
          draftFormat.current = next.format
          onDraft(next.text)
          text.field.onChange(next.text)
          format.field.onChange(next.format)
        }}
      />
    </Suspense>
  )
}

interface IProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>
  valName: Path<TFieldValues>
  label?: string
  getValues: UseFormGetValues<TFieldValues> | undefined
  required?: boolean
  color?: string
  helperText?: string
  placeholder?: string
  style?: React.CSSProperties
  onChangeCustom?: (e: SyntheticEvent, onChange: (value: string) => void) => void
  disabled?: boolean
  /**
   * Server-localized twin of this field. The user reads and edits the text in
   * their own language: the translation is prefilled, and saving stores it as
   * the text itself, making the editor's language the new original (WA-762).
   */
  l10n?: L10nFragment | null
  /**
   * The field holding this text's format (TextFormat). With it the text is read as Markdown when
   * the format says so and edited in the rich editor, which writes the text and its format as a
   * pair; without it the field stays the plain text box it always was.
   */
  formatName?: Path<TFieldValues>
}

const CTextAreaWithEdit = <TFieldValues extends FieldValues = FieldValues>(props: IProps<TFieldValues>) => {
  const {
    control,
    valName,
    getValues,
    required,
    color,
    helperText,
    placeholder,
    label,
    style,
    onChangeCustom,
    disabled,
    l10n,
    formatName,
  } = props
  const { t } = useTranslation()
  // Whatever the form holds under this path; only a string is text to this field.
  const val: unknown = getValues?.(valName)
  const valText = typeof val === "string" ? val : null
  const [isEditing, setIsEditing] = useState(!val)
  const [showOriginal, setShowOriginal] = useState(false)
  // What the user has typed since entering edit mode; null until the first keystroke.
  const [draft, setDraft] = useState<string | null>(null)
  // Every keystroke hands the draft to the form too, so the draft is also the
  // value we last wrote there. When the form value changes to anything else, the
  // record itself was swapped (another entry, a refetch or an external reset)
  // and the stale draft must not leak into it. An `onChangeCustom` that stores
  // a transformed value makes that true on every keystroke: the field then shows
  // what the form holds.
  if (draft !== null && valText !== draft) {
    setDraft(null)
  }
  const { isIOSApp } = useDevice()
  const l10nView = resolveL10n(l10n, valText ?? "")
  // Editing a machine translation replaces the author's original on save.
  const editsTranslation = l10nView.badge === "translated" && l10nView.canShowOriginal
  const placeholderText = placeholder || t("common.placeholderWorkout") || ""
  const renderReadOnly = (text: string, isMarkdown = false) => {
    // A twin describes the stored words. After an edit, preview shows the draft in its own format.
    const readView = resolveL10n(l10n && text !== l10n.original ? null : l10n, text)
    return (
      <>
        <div className="min-h-[64px] bg-customGray-100 border rounded-lg p-2 cursor-text text-sm font-normal leading-4 whitespace-pre-wrap overflow-hidden wrap-anywhere">
          <ReadOnlyText
            text={showOriginal ? readView.original : readView.display}
            isMarkdown={isMarkdown}
            isIOSApp={isIOSApp}
            emptyText={t("common.placeholderWorkout")}
          />
        </div>
        <L10nNote
          view={readView}
          showingOriginal={showOriginal}
          onToggle={() => setShowOriginal(!showOriginal)}
        />
      </>
    )
  }
  return (
    <div className="relative">
      <div className="absolute right-0 -top-2 flex items-center bg-white p-1 pl-2.5 rounded-full">
        <PencilGrayIcon className="mr-2.5" />
        <CToggleSwitch
          checked={isEditing}
          onChange={() => setIsEditing(!isEditing)}
        />
      </div>
      {label && (
        <CLabel className="block max-md:max-w-52 mb-2 text-gray-900 text-sm font-medium leading-4">{label}</CLabel>
      )}
      <div>
        {formatName !== undefined ? (
          <>
            <RichTextField<TFieldValues>
              control={control}
              valName={valName}
              formatName={formatName}
              required={required}
              // The same rule as the text box below: the text the user reads, untouched until edited.
              shownText={draft ?? (editsTranslation ? l10nView.display : null)}
              placeholder={placeholderText}
              disabled={disabled}
              onDraft={setDraft}
              isEditing={isEditing}
              renderReadOnly={renderReadOnly}
            />
            {isEditing && editsTranslation && <L10nEditWarning />}
          </>
        ) : isEditing ? (
          <Controller
            control={control}
            name={valName}
            rules={{ required }}
            render={({ field: { value, onChange } }) => (
              <>
                <CAutoSizeTextArea
                  color={color}
                  helperText={helperText}
                  height={136}
                  // Edit the text the user actually reads. `draft` stays null
                  // until the first keystroke, so the form value (the author's
                  // original) is untouched — and therefore not dirty — while the
                  // user merely looks at the field.
                  value={draft ?? (editsTranslation ? l10nView.display : value || "")}
                  onChange={(e) => {
                    setDraft((e.target as HTMLTextAreaElement).value)
                    return onChangeCustom
                      ? onChangeCustom(e, onChange)
                      : onChange((e.target as HTMLTextAreaElement).value)
                  }}
                  placeholder={placeholderText}
                  style={style}
                  disabled={disabled}
                  // Match the read-only block: long unbroken strings (server
                  // text without spaces, pasted IDs, etc.) must wrap by
                  // character. Native <textarea wrap="soft"> only breaks at
                  // whitespace, so without this the string just gets clipped
                  // by overflow-hidden and the user thinks input is missing.
                  className="wrap-anywhere"
                />
                {editsTranslation && <L10nEditWarning />}
              </>
            )}
          />
        ) : (
          renderReadOnly(valText ?? "")
        )}
      </div>
    </div>
  )
}

export default CTextAreaWithEdit
